import { useEffect, useState, useRef } from "react";
import { useWorkflowStore } from "./workflowStore";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import VersionEditor from "./VersionEditor";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Textarea } from "src/sudokit/components/Textarea";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import Auth from "src/Auth";
import { useToast } from "src/components/ToastWrapper/index.js";
import CompletedVersion from "./CompletedVersion";

function WorkflowDetail() {
  const { workflowId, versionId } = useParams();
  const history = useHistory();
  const workflow = useWorkflowStore((state) => state.workflows[workflowId]);
  const updateWorkflow = useWorkflowStore((state) => state.updateWorkflow);
  const creatingNewVersion = useWorkflowStore((state) => state.creatingNewVersion);
  const createNewVersion = useWorkflowStore((state) => state.createNewVersion);
  const [editingWorkflow, setEditingWorkflow] = useState(false);
  const [workflowName, setWorkflowName] = useState(workflow.name);
  const [workflowDescription, setWorkflowDescription] = useState(workflow.description);
  const [saving, setSaving] = useState(false);
  const selectRef = useRef(null);

  const { setToast } = useToast();

  useEffect(() => {
    if (!versionId) {
      if (workflow?.productionVersionId) {
        history.replace(`/ai-functions/${workflowId}/v/${workflow.productionVersionId}`);
      } else if (workflow?.versions?.[0]?.id) {
        history.replace(`/ai-functions/${workflowId}/v/${workflow.versions[0].id}`);
      }
    }
  }, [workflow, versionId, history, workflowId]);

  const handleSetProduction = async (e, versionId) => {
    e.preventDefault();
    const result = await Auth.fetchWithIdToken(`/api/workflows/${workflowId}`, {
      productionVersionId: versionId,
    });

    updateWorkflow(result.workflow);
    setToast(versionId ? "Version set as production successfully" : "Version unset as production successfully");
  };

  const isProduction = workflow.productionVersionId === versionId;
  const isSystem = workflow.isSystem;

  if (!workflow) {
    return <div>Workflow not found</div>;
  }

  const selectedVersion = workflow.versions.find((v) => v.id === versionId);
  const versions = workflow.versions.sort((a, b) => a.version - b.version);
  const versionOptions = versions.map((version) => ({
    label: `Version ${version.version} created ${moment(version.createdAt).format("MMMM Do YYYY h:mm a")} ${workflow.productionVersionId === version.id ? "(prod)" : ""}`,
    value: version.id,
  }));

  const handleSave = async () => {
    setSaving(true);
    const result = await Auth.fetchWithIdToken(`/api/workflows/${workflowId}`, {
      name: workflowName,
      description: workflowDescription,
    });
    updateWorkflow(result.workflow);
    setEditingWorkflow(false);
    setSaving(false);
  };
  return (
    <div className="flex flex-col w-full">
      {!editingWorkflow && (
        <div className="flex justify-between items-center">
          <div>
            <p style={{ fontSize: "2rem" }}>
              {workflow.name || workflow.slug}{" "}
              <SudoButton
                variant="tertiaryGrey"
                onClick={() => {
                  setEditingWorkflow(true);
                  setWorkflowName(workflow.name);
                  setWorkflowDescription(workflow.description);
                }}
              >
                Edit
              </SudoButton>
            </p>
            <p>{workflow.description}</p>
          </div>
          <div className="flex gap-2">
            <SudoButton
              onClick={async (e) => {
                e.preventDefault();
                const result = await createNewVersion(workflowId, versionId);
                updateWorkflow(result.workflow);
                history.replace(`/ai-functions/${workflowId}/v/${result.versionId}`);
              }}
              disabled={creatingNewVersion}
              variant="tertiary"
            >
              New Version
            </SudoButton>
            {!isProduction && isSystem && (
              <SudoButton
                variant="tertiary"
                onClick={(e) => handleSetProduction(e, versionId)}
              >
                Activate Version
              </SudoButton>
            )}
            {isProduction && isSystem && (
              <SudoButton
                variant="secondary"
                onClick={(e) => handleSetProduction(e, null)}
              >
                Deactivate Version
              </SudoButton>
            )}
          </div>
        </div>
      )}
      {editingWorkflow && (
        <div>
          <label htmlFor="name">Name</label>
          <input
            id="name"
            type="text"
            name="name"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
          />
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="description"
          >
            Description
          </label>
          <Textarea
            id="description"
            type="text"
            name="description"
            placeholder="The goal of this workflow is to..."
            value={workflowDescription}
            onChange={(e) => setWorkflowDescription(e.target.value)}
          />
          <div className="flex gap-2">
            <SudoButton
              variant="tertiaryGrey"
              onClick={() => setEditingWorkflow(false)}
              disabled={saving}
            >
              Cancel
            </SudoButton>
            <SudoButton
              variant="tertiary"
              onClick={() => handleSave()}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </SudoButton>
          </div>
        </div>
      )}
      {!workflow.productionVersionId && isSystem && <p className="text-sm text-red-500">No active version set</p>}

      <div className="mt-4">
        {versionId && (
          <MenuSelect
            ref={selectRef}
            id="version-select"
            variant="secondary"
            value={versionId || ""}
            onChange={(value) => {
              history.replace(`/ai-functions/${workflowId}/v/${value}`);
            }}
            optionAlignment="left"
            options={versionOptions}
            menuType="button"
          />
        )}
      </div>

      {!selectedVersion?.completedAt && (
        <VersionEditor
          key={versionId}
          workflowId={workflowId}
          versionId={versionId}
        />
      )}
      {selectedVersion?.completedAt && (
        <CompletedVersion
          workflowId={workflowId}
          version={selectedVersion}
        />
      )}
    </div>
  );
}

export default WorkflowDetail;
